import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { DjayAvatar, useDjaySelection } from '../../mascot';
import { useAppStore } from '../../app/hooks/useAppStore';
import { MODULES, getFamiliesForModule, getAllowedTiersForGroup, getModuleIcon, getModuleColors } from '../../content';
import { getFamilyMasteryKey } from '../../mastery';
import { CardSurface } from '../shared/CardSurface';
import { MasteryProgressBar } from '../shared/MasteryProgressBar';
import { CenteredModal } from '../shared/CenteredModal';
import { MODE_THEME } from '../shared/modeTheme';
interface FluencyModulePickerProps {
  isOpen: boolean;
  onSelectAll: () => void;
  onSelectModule: (moduleId: string) => void;
  onCancel: () => void;
}
export function FluencyModulePicker({
  isOpen,
  onSelectAll,
  onSelectModule,
  onCancel
}: FluencyModulePickerProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  const {
    state,
    selectedGroup
  } = useAppStore();
  const familyMastery = state.familyMastery || {};
  const fluencyTheme = MODE_THEME['fluency'];
  const allowedTiers = useMemo(() => getAllowedTiersForGroup(selectedGroup), [selectedGroup]);
  const moduleAggregates = useMemo(() => {
    return MODULES.map(module => {
      const families = getFamiliesForModule(module.id);
      const moduleFamilies = families.filter(family => {
        if (!allowedTiers.has(family.tier)) return false;
        const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup);
        const mastery = familyMastery[masteryKey];
        return mastery && mastery.attempts > 0;
      });
      const count = moduleFamilies.length;
      const avgMastery = count > 0 ? moduleFamilies.reduce((sum, f) => {
        const masteryKey = getFamilyMasteryKey(f.familyId, selectedGroup);
        return sum + (familyMastery[masteryKey]?.masteryScore ?? 0);
      }, 0) / count : 0;
      return {
        module,
        count,
        avgMastery
      };
    }).filter(m => m.count > 0);
  }, [selectedGroup, familyMastery, allowedTiers]);
  return <CenteredModal isOpen={isOpen} onClose={onCancel} maxWidth="3xl" ariaLabel="Kies een onderdeel om te oefenen" panelClassName="bg-slate-50 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-3116" data-mp="1w4a2wvrrmvd4">
      
      {/* Header */}
      <div className={`bg-gradient-to-r ${fluencyTheme.gradient} p-6 text-white text-center relative flex-shrink-0`} data-id="element-3117" data-mp="2a42pqg068rft">
        
        <button type="button" onClick={onCancel} aria-label="Sluiten" className="absolute top-3 right-3 w-11 h-11 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-3118" data-mp="2f1uifc8n4hl2">
          
          <X className="w-5 h-5" aria-hidden="true" data-id="element-3119" data-mp="2sv29n2st7b28" />
        </button>
        <div className="flex justify-center mb-3" data-id="element-3120" data-mp="2xtdknguubrm1">
          <DjayAvatar size="md" emotion="pointing" variant={selectedVariant} animate={true} data-id="element-3121" data-mp="2xu5ikybwu1fh" />
          
        </div>
        <h2 className="text-2xl font-black tracking-tight" data-id="element-3122" data-mp="m7br3dg8bjuy">
          Wat wil je oefenen?
        </h2>
      </div>

      {/* Content */}
      <div className="p-6 sm:p-8 overflow-y-auto" data-id="element-3123" data-mp="d52f0j5bbifg">
        <Button variant="primary" onClick={onSelectAll} className={`w-full py-4 mb-8 bg-gradient-to-r ${fluencyTheme.gradient} hover:brightness-110 border-none shadow-lg ${fluencyTheme.shadow} text-lg`} data-id="element-3124" data-mp="d5vohrvnom1a">
          
          <Sparkles className="w-5 h-5 mr-2" data-id="element-3125" data-mp="14ymcxg73l6fz" />
          Alles door elkaar
        </Button>

        {moduleAggregates.length > 0 && <div data-id="element-3126" data-mp="w47zlj64o5zi">
            <h3 className="text-lg font-black text-slate-800 mb-4 text-center" data-id="element-3127" data-mp="w3ofpwwx37iu">
              Of kies een specifiek vak
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-3128" data-mp="r4ln5by4itrr">
              {moduleAggregates.map(({
            module,
            count,
            avgMastery
          }, index) => {
            const ModIcon = getModuleIcon(module.icon);
            const colors = getModuleColors(module.color);
            return <CardSurface key={module.id} onClick={() => onSelectModule(module.id)} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: Math.min(index * 0.05, 0.3)
            }} className="w-full text-left p-4 rounded-3xl border-2 border-slate-200 bg-white hover:border-slate-300 hover:shadow-lg transition-colors group relative overflow-hidden flex flex-col h-full" data-id="element-3129" data-mp="13cymj30v5q29" data-mpi={index}>
                  
                    <div className="flex items-start justify-between mb-3" data-id="element-3130" data-mp="1r5owx7t4c1af">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center ${colors.light}`} data-id="element-3131" data-mp="1dc7btiksagia">
                      
                        <ModIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${colors.text}`} data-id="element-3132" data-mp="260v07n0txm02" />
                      
                      </div>
                      <div className="bg-slate-100 text-slate-500 text-[10px] font-bold px-2 py-1 rounded-lg" data-id="element-3133" data-mp="2ategwsutcx93">
                        {count} items
                      </div>
                    </div>

                    <h3 className="text-sm sm:text-base font-black text-slate-800 mb-1 group-hover:text-slate-900 transition-colors" data-id="element-3134" data-mp="2g4tauu9o5ejt">
                      {module.title}
                    </h3>

                    <div className="mt-auto pt-3" data-id="element-3135" data-mp="2oy59riui6519">
                      <MasteryProgressBar masteryScore={avgMastery} data-id="element-3136" data-mp="21798lv45pxhc" />
                    </div>
                  </CardSurface>;
          })}
            </div>
          </div>}
      </div>
    </CenteredModal>;
}